/**
 * TimeTrackingContext.tsx — global time-tracking state + actions.
 *
 * Owns: the currently-running `ActiveTimer`, recent-projects + recent-tasks
 *       cache, start / stop / switch-task actions, the Ctrl/Cmd+Shift+T
 *       global hotkey for stopping a running timer, calendar-event
 *       integration for Google Calendar, and the recent-timer history
 *       persisted to localStorage.
 * Used by: every component that needs to read or mutate the running
 *       timer — `TimeTrackingBoard`, the running-timer chip in App.tsx,
 *       per-task time entry cells.
 * Depends on: `services/api.ts` (timeTrackingApi, googleCalendarApi),
 *       `AuthContext` for the current user, `ToastContext` for feedback,
 *       `utils/recentTimers` for the localStorage helper.
 * Notes: 511 LOC — under the >=1,000 god-file cutoff but included in this
 *       ticket because a 0b-03 component deletion left a stale comment in
 *       the Ctrl/Cmd+Shift+T hotkey handler. Header + comment fix added
 *       during 0b-08.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { timeTrackingApi, googleCalendarApi, ActiveTimer, TimeProject, TimeTask, TimeEntry, CalendarEvent } from '../services/api';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';
import { saveRecentTimer } from '../utils/recentTimers';
import { extractApiError } from '../types/apiError';
import { setTabTakeover } from '../hooks/usePageTitle';
import AccountHealthBanner from '../components/system/AccountHealthBanner';

interface StoppedEntryInfo {
  projectName: string;
  taskName: string;
  durationSeconds: number;
  entryId?: number;
}

interface TimeTrackingContextType {
  activeTimer: ActiveTimer | null;
  projects: TimeProject[];
  tasks: TimeTask[];
  favorites: Set<number>;
  projectTaskMap: Record<number, number[]>;
  loading: boolean;
  idleWarning: boolean;
  lastStoppedEntry: StoppedEntryInfo | null;
  upcomingEvents: CalendarEvent[];
  calendarConnected: boolean | null;
  dismissedEventIds: Set<string>;
  dismissIdleWarning: () => void;
  stopTimerAndSubtractIdle: () => Promise<void>;
  dismissLastStopped: () => void;
  dismissEvent: (eventId: string) => void;
  startTimer: (projectId: number, taskId: number) => Promise<void>;
  stopTimer: () => Promise<void>;
  switchTask: (projectId: number, taskId: number) => Promise<void>;
  /** Flip the pill to a freshly-started entry immediately (optimistic), then
   *  reconcile in the background. For the header tracker's client-start path. */
  applyStartedTimer: (entry: TimeEntry, projectName?: string) => void;
  toggleFavorite: (projectId: number) => Promise<void>;
  getTasksForProject: (projectId: number) => TimeTask[];
  refreshTimer: () => Promise<void>;
  refreshProjects: () => Promise<void>;
  refreshTasks: () => Promise<void>;
  refreshProjectTaskMap: () => Promise<void>;
  refreshCalendar: () => Promise<void>;
}

const TimeTrackingContext = createContext<TimeTrackingContextType | null>(null);

// ─── Perf Phase 3d — the ticking second lives in its OWN context ───────────
//
// `elapsedSeconds` increments once per second while a timer runs. It used to
// sit in the main context value (a dep of the value useMemo), so every tick
// minted a new value object and re-rendered EVERY useTimeTracking() consumer —
// including AppContent in App.tsx, which renders the entire authenticated app.
// With a timer running on a board, that was a full-tree re-render, once per
// second, for the duration of the tracked hour (~3,600 of them).
//
// Splitting the field is NOT sufficient on its own: stopTimer/switchTask
// close over the elapsed value for their logged-duration fallback, and if
// `elapsedSeconds` stayed in THEIR dep arrays their identities would churn
// every second and drag the value memo with them. They read `elapsedRef`
// instead (kept in lockstep with the state by `setElapsed`), which is why
// this context + that ref ship as one change.
//
// Consumers that display the clock subscribe here via useElapsedSeconds();
// everything else subscribes to the main context and stops ticking.
const ElapsedSecondsContext = createContext<number>(0);

export function TimeTrackingProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const toast = useToast();
  const [activeTimer, setActiveTimer] = useState<ActiveTimer | null>(null);
  const [projects, setProjects] = useState<TimeProject[]>([]);
  const [tasks, setTasks] = useState<TimeTask[]>([]);
  const [favorites, setFavorites] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [idleWarning, setIdleWarning] = useState(false);
  const [lastStoppedEntry, setLastStoppedEntry] = useState<StoppedEntryInfo | null>(null);
  const [upcomingEvents, setUpcomingEvents] = useState<CalendarEvent[]>([]);
  const [calendarConnected, setCalendarConnected] = useState<boolean | null>(null);
  const [dismissedEventIds, setDismissedEventIds] = useState<Set<string>>(new Set());
  const [projectTaskMap, setProjectTaskMap] = useState<Record<number, number[]>>({});
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Mirror of elapsedSeconds for the stop/switch actions (perf 3d): they need
  // the CURRENT elapsed value at click time without depending on the ticking
  // state (which would re-mint their identities every second). Always written
  // through setElapsed so ref and state cannot drift.
  const elapsedRef = useRef(0);
  const lastActivityRef = useRef(Date.now());
  const idleCheckRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const IDLE_THRESHOLD_MS = 15 * 60 * 1000; // 15 minutes
  // Generation token for active-timer writes: every getActiveTimer captures the
  // current seq; a deliberate mutation (start/stop/switch) bumps it. A poll that
  // resolves after a mutation is then discarded instead of resurrecting a
  // just-stopped timer or clobbering a just-started one (last-write-by-intent).
  const seqRef = useRef(0);
  // Re-entry guard so a rapid double-click can't double-fire start/stop/switch.
  const mutatingRef = useRef(false);

  // The ONLY writer of elapsed — keeps the ref (read by stop/switch at click
  // time) and the state (read by the display components via
  // useElapsedSeconds) in lockstep. Stable identity, safe in dep arrays.
  const setElapsed = useCallback((n: number) => {
    elapsedRef.current = n;
    setElapsedSeconds(n);
  }, []);

  // Build an ActiveTimer from a started/switched entry so the pill can flip
  // optimistically without a second GET round-trip (is_internal reconciles on
  // the background refresh).
  const buildActiveTimer = (entry: TimeEntry, projectName?: string): ActiveTimer => ({
    entry_id: entry.id,
    user_id: entry.user_id,
    project_id: entry.project_id,
    task_id: entry.task_id,
    project_name: projectName || entry.project_name || '',
    task_name: entry.task_name || '',
    start_time: entry.start_time,
    is_internal: false,
  });

  const refreshTimer = useCallback(async () => {
    if (!user) return;
    const mySeq = ++seqRef.current;
    try {
      const res = await timeTrackingApi.getActiveTimer();
      // Only apply if no newer poll OR deliberate mutation happened meanwhile —
      // otherwise a late poll could resurrect a just-stopped timer.
      if (res.success && mySeq === seqRef.current) {
        setActiveTimer(res.data);
      }
    } catch {
      // Silent fail for polling
    }
  }, [user]);

  // Flip the pill to a freshly-started timer immediately (no second round-trip),
  // then reconcile in the background. Used by the header tracker's start path.
  const applyStartedTimer = useCallback((entry: TimeEntry, projectName?: string) => {
    seqRef.current += 1; // invalidate any in-flight poll so it can't overwrite us
    setActiveTimer(buildActiveTimer(entry, projectName));
    setElapsed(0);
    void refreshTimer(); // background reconcile (is_internal, server truth)
  }, [refreshTimer, setElapsed]);

  const refreshProjects = useCallback(async () => {
    try {
      const res = await timeTrackingApi.getProjects(true);
      if (res.success) setProjects(res.data);
    } catch { /* silent */ }
  }, []);

  const refreshTasks = useCallback(async () => {
    try {
      const res = await timeTrackingApi.getTasks(true);
      if (res.success) setTasks(res.data);
    } catch { /* silent */ }
  }, []);

  const refreshProjectTaskMap = useCallback(async () => {
    try {
      const res = await timeTrackingApi.getProjectTaskMap();
      if (res.success) setProjectTaskMap(res.data);
    } catch { /* silent */ }
  }, []);

  const getTasksForProject = useCallback((projectId: number): TimeTask[] => {
    const enabledIds = projectTaskMap[projectId];
    if (!enabledIds || enabledIds.length === 0) return tasks;
    const idSet = new Set(enabledIds);
    return tasks.filter(t => idSet.has(t.id));
  }, [projectTaskMap, tasks]);

  const refreshFavorites = useCallback(async () => {
    try {
      const res = await timeTrackingApi.getFavorites();
      if (res.success) setFavorites(new Set(res.data));
    } catch { /* silent */ }
  }, []);

  const refreshCalendar = useCallback(async () => {
    if (!user) return;
    try {
      const res = await googleCalendarApi.getTodayEvents();
      if (res.success) {
        setCalendarConnected(res.data.connected);
        setUpcomingEvents(res.data.events);
      }
    } catch {
      // Silent fail — if calendar not connected, just don't show notifications
    }
  }, [user]);

  const dismissEvent = useCallback((eventId: string) => {
    setDismissedEventIds(prev => new Set(prev).add(eventId));
  }, []);

  // Compute elapsed seconds from active timer
  useEffect(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }

    if (!activeTimer) {
      setElapsed(0);
      setIdleWarning(false);
      return;
    }

    const computeElapsed = () => {
      const start = new Date(activeTimer.start_time).getTime();
      const now = Date.now();
      const elapsed = Math.max(0, Math.round((now - start) / 1000));
      setElapsed(elapsed);
    };

    computeElapsed();
    tickRef.current = setInterval(computeElapsed, 1000);

    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [activeTimer, setElapsed]);

  // Idle detection: activity-based (mousemove, keydown, click) — warn after 15 min idle
  useEffect(() => {
    if (idleCheckRef.current) {
      clearInterval(idleCheckRef.current);
      idleCheckRef.current = null;
    }

    if (!activeTimer) return;

    const updateActivity = () => lastActivityRef.current = Date.now();

    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('click', updateActivity);

    idleCheckRef.current = setInterval(() => {
      if (activeTimer && Date.now() - lastActivityRef.current > IDLE_THRESHOLD_MS) {
        setIdleWarning(true);
      }
    }, 60000); // check every minute

    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('click', updateActivity);
      if (idleCheckRef.current) clearInterval(idleCheckRef.current);
    };
  }, [activeTimer]);

  const dismissIdleWarning = useCallback(() => {
    lastActivityRef.current = Date.now();
    setIdleWarning(false);
  }, []);

  const stopTimerAndSubtractIdle = useCallback(async () => {
    if (!activeTimer) return;
    setLoading(true);
    try {
      const res = await timeTrackingApi.stopTimer();
      setActiveTimer(null);
      setElapsed(0);
      setIdleWarning(false);
      if (res.success && res.data?.id) {
        const entry = res.data;
        const startMs = new Date(entry.start_time).getTime();
        const durationMs = (entry.duration_seconds ?? 0) * 1000;
        const subtractMs = 15 * 60 * 1000;
        const newDurationMs = Math.max(0, durationMs - subtractMs);
        const newEndTime = new Date(startMs + newDurationMs).toISOString();
        await timeTrackingApi.updateEntry(entry.id, { end_time: newEndTime });
        setLastStoppedEntry({
          projectName: activeTimer.project_name,
          taskName: activeTimer.task_name,
          durationSeconds: Math.round(newDurationMs / 1000),
          entryId: entry.id,
        });
      }
    } catch (err: any) {
      toast.error(extractApiError(err, 'Failed to stop timer'));
    } finally {
      setLoading(false);
    }
  }, [activeTimer, toast, setElapsed]);
  const dismissLastStopped = useCallback(() => setLastStoppedEntry(null), []);

  // Load on mount and when user changes
  useEffect(() => {
    if (user) {
      refreshTimer();
      refreshProjects();
      refreshTasks();
      refreshProjectTaskMap();
      refreshFavorites();
      refreshCalendar();
    } else {
      setActiveTimer(null);
      setProjects([]);
      setTasks([]);
      setProjectTaskMap({});
      setFavorites(new Set());
      setUpcomingEvents([]);
      setCalendarConnected(null);
      setDismissedEventIds(new Set());
    }
  }, [user, refreshTimer, refreshProjects, refreshTasks, refreshProjectTaskMap, refreshFavorites, refreshCalendar]);

  // Global calendar polling — every 2 minutes, regardless of page
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(refreshCalendar, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user, refreshCalendar]);

  // Re-poll on window focus
  useEffect(() => {
    const onFocus = () => { refreshTimer(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshTimer]);

  // Poll every 60s to stay in sync with other tabs
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(refreshTimer, 60000);
    return () => clearInterval(interval);
  }, [user, refreshTimer]);

  const startTimerAction = useCallback(async (projectId: number, taskId: number) => {
    if (mutatingRef.current) return; // ignore rapid double-fire
    mutatingRef.current = true;
    setLoading(true);
    try {
      const res = await timeTrackingApi.startTimer(projectId, taskId);
      if (res.success) {
        // Optimistic flip from the start response; reconcile in the background.
        if (res.data?.entry) {
          seqRef.current += 1;
          setActiveTimer(buildActiveTimer(res.data.entry));
          setElapsed(0);
          void refreshTimer();
        } else {
          await refreshTimer();
        }
        const project = projects.find((p) => p.id === projectId);
        const task = tasks.find((t) => t.id === taskId);
        if (project && task) {
          saveRecentTimer({
            projectId,
            projectName: project.name,
            taskId,
            taskName: task.name,
          });
        }
      } else {
        toast.error((res as { error?: string }).error || 'Failed to start timer');
      }
    } catch (err: any) {
      toast.error(extractApiError(err, 'Failed to start timer'));
    } finally {
      mutatingRef.current = false;
      setLoading(false);
    }
  }, [refreshTimer, projects, tasks, toast, setElapsed]);

  const stopTimerAction = useCallback(async () => {
    if (mutatingRef.current || !activeTimer) return; // guard double-click / no-op
    const stoppedTimer = activeTimer;
    // elapsedRef, NOT elapsedSeconds (perf 3d): depending on the ticking state
    // would re-mint this callback every second and drag the value memo with it.
    const stoppedElapsed = elapsedRef.current;
    mutatingRef.current = true;
    seqRef.current += 1;        // invalidate in-flight polls so they can't resurrect
    setActiveTimer(null);       // optimistic clear — stop feels instant
    setElapsed(0);
    setLoading(true);
    try {
      const res = await timeTrackingApi.stopTimer();
      setLastStoppedEntry({
        projectName: stoppedTimer.project_name,
        taskName: stoppedTimer.task_name,
        // Prefer the server's authoritative duration; fall back to client elapsed.
        durationSeconds: res.data?.duration_seconds ?? stoppedElapsed,
        entryId: res.data?.id,
      });
    } catch (err: any) {
      // 404 = the timer was already stopped (e.g. a double-click's second call):
      // the desired end state is already true, so treat it as success, not an error.
      if (err?.response?.status === 404) {
        setLastStoppedEntry({
          projectName: stoppedTimer.project_name,
          taskName: stoppedTimer.task_name,
          durationSeconds: stoppedElapsed,
        });
      } else {
        // A real failure — roll back the optimistic clear so UI matches the server.
        setActiveTimer(stoppedTimer);
        toast.error(extractApiError(err, 'Failed to stop timer'));
      }
    } finally {
      mutatingRef.current = false;
      setLoading(false);
    }
  }, [activeTimer, toast, setElapsed]);

  const switchTaskAction = useCallback(async (projectId: number, taskId: number) => {
    if (mutatingRef.current) return; // ignore rapid double-fire
    const stoppedTimer = activeTimer;
    const stoppedElapsed = elapsedRef.current; // ref, not state — see stopTimerAction
    mutatingRef.current = true;
    setLoading(true);
    try {
      const res = await timeTrackingApi.switchTask(projectId, taskId);
      if (res.success) {
        if (stoppedTimer) {
          setLastStoppedEntry({
            projectName: stoppedTimer.project_name,
            taskName: stoppedTimer.task_name,
            durationSeconds: stoppedElapsed,
            entryId: res.data?.stoppedEntry?.id,
          });
        }
        // Optimistic flip to the new task; reconcile in the background.
        if (res.data?.newEntry) {
          seqRef.current += 1;
          setActiveTimer(buildActiveTimer(res.data.newEntry));
          setElapsed(0);
          void refreshTimer();
        } else {
          await refreshTimer();
        }
      } else {
        toast.error((res as { error?: string }).error || 'Failed to switch task');
      }
    } catch (err: any) {
      toast.error(extractApiError(err, 'Failed to switch task'));
    } finally {
      mutatingRef.current = false;
      setLoading(false);
    }
  }, [refreshTimer, activeTimer, toast, setElapsed]);

  const toggleFavoriteAction = useCallback(async (projectId: number) => {
    try {
      const res = await timeTrackingApi.toggleFavorite(projectId);
      if (res.success) {
        setFavorites(prev => {
          const next = new Set(prev);
          if (res.data.isFavorite) {
            next.add(projectId);
          } else {
            next.delete(projectId);
          }
          return next;
        });
      } else {
        toast.error((res as { error?: string }).error || 'Failed to update favorite');
      }
    } catch (err: any) {
      toast.error(extractApiError(err, 'Failed to update favorite'));
    }
  }, [toast]);

  // Browser tab title + favicon: flickering red dot / logo when recording, logo when idle
  useEffect(() => {
    const setFavicon = (href: string) => {
      let link = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.type = 'image/png';
      if (link.href !== href) link.href = href;
    };

    // BULK-1 WP-Calls: the tab's title goes through the one shared writer
    // (hooks/usePageTitle). A running timer still takes the tab over; when it
    // stops, the tab goes back to the record on screen (the deal, contact,
    // company or project), else to the app's name.
    if (!activeTimer) {
      setTabTakeover(null);
      setFavicon('/logo.png');
      return;
    }

    // While tracking, the tab carries a live title (elapsed \u00B7 client \u2014 task) +
    // a clean PULSATING paprika dot favicon (a soft breathe \u2014 scale + opacity \u2014
    // not a hard blink; transparent background, no muddy ring). Frames are
    // pre-rendered once, then cycled, so it stays cheap.
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d')!;
    // Pulse OPACITY only at a CONSTANT size \u2014 a size change reads as jumpy at
    // favicon resolution; a steady dot fading in/out looks smooth. Fewer, evenly
    // stepped frames + a snappier ~1.1s cycle (the old 2.2s size-breathe felt
    // slow + choppy).
    // Perf 3d: 9 frames at 120ms, not 18 at 60ms. Same ~1.1s breathe cycle,
    // half the favicon href swaps (each one decodes a data-URI PNG and
    // repaints the tab icon — this ran 16.7×/sec for the whole tracked hour).
    // At 16px tab-icon resolution the coarser opacity steps are imperceptible.
    // Chrome throttles background-tab timers to ≥1s anyway, so the full rate
    // was only ever paid by the foreground tab.
    const FRAMES = 9;
    const frames: string[] = [];
    for (let i = 0; i < FRAMES; i++) {
      const breathe = 0.5 - 0.5 * Math.cos((i / FRAMES) * 2 * Math.PI); // 0\u21921\u21920
      const alpha = 0.38 + breathe * 0.62; // .38 \u2192 1
      ctx.clearRect(0, 0, 32, 32);
      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.arc(16, 16, 12, 0, Math.PI * 2); // constant radius
      ctx.fillStyle = '#f76100'; // paprika
      ctx.fill();
      ctx.globalAlpha = 1;
      frames.push(canvas.toDataURL('image/png'));
    }

    let frame = 0;
    let lastSec = -1;
    const tick = () => {
      const start = new Date(activeTimer.start_time).getTime();
      const elapsed = Math.max(0, Math.floor((Date.now() - start) / 1000));
      if (elapsed !== lastSec) {
        lastSec = elapsed;
        const h = Math.floor(elapsed / 3600);
        const m = Math.floor((elapsed % 3600) / 60);
        const s = elapsed % 60;
        const time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
        setTabTakeover(`${time} \u00B7 ${activeTimer.project_name} \u2014 ${activeTimer.task_name}`);
      }
      setFavicon(frames[frame % FRAMES]);
      frame += 1;
    };

    tick();
    const interval = setInterval(tick, 120); // 9 \u00D7 120ms \u2248 1.1s cycle (perf 3d)

    return () => {
      clearInterval(interval);
      setTabTakeover(null);
      setFavicon('/logo.png');
    };
  }, [activeTimer]);

  // Keyboard shortcuts: Ctrl+Shift+T to toggle timer
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'T') {
        e.preventDefault();
        if (activeTimer) {
          stopTimerAction();
        }
        // No-op when no timer is running — start-timer flow lives in
        // TimeTrackingBoard, reached via the sidebar.
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeTimer, stopTimerAction]);

  const value = useMemo(() => ({
    activeTimer,
    projects,
    tasks,
    favorites,
    projectTaskMap,
    loading,
    idleWarning,
    lastStoppedEntry,
    upcomingEvents,
    calendarConnected,
    dismissedEventIds,
    dismissIdleWarning,
    stopTimerAndSubtractIdle,
    dismissLastStopped,
    dismissEvent,
    startTimer: startTimerAction,
    stopTimer: stopTimerAction,
    switchTask: switchTaskAction,
    applyStartedTimer,
    toggleFavorite: toggleFavoriteAction,
    getTasksForProject,
    refreshTimer,
    refreshProjects,
    refreshTasks,
    refreshProjectTaskMap,
    refreshCalendar,
  }), [
    activeTimer,
    projects,
    tasks,
    favorites,
    projectTaskMap,
    loading,
    idleWarning,
    lastStoppedEntry,
    upcomingEvents,
    calendarConnected,
    dismissedEventIds,
    dismissIdleWarning,
    stopTimerAndSubtractIdle,
    dismissLastStopped,
    dismissEvent,
    startTimerAction,
    stopTimerAction,
    switchTaskAction,
    applyStartedTimer,
    toggleFavoriteAction,
    getTasksForProject,
    refreshTimer,
    refreshProjects,
    refreshTasks,
    refreshProjectTaskMap,
    refreshCalendar,
  ]);

  return (
    <TimeTrackingContext.Provider value={value}>
      {/* Perf 3d: the ticking second is provided SEPARATELY so its 1 Hz
          updates reach only the components that render the clock. The main
          value above no longer changes per second, so React bails out of the
          {children} subtree on every tick (same element reference + same
          context value = no work). */}
      <ElapsedSecondsContext.Provider value={elapsedSeconds}>
        {children}
        {/* The time-tracker now lives INLINE in the top app-bar Toolbar
            (App.tsx) — Mike: "don't make it frozen on screen". It's no longer
            mounted here as a floating element. */}
        {/* Entry 184: persistent "reconnect your account" banner. Global-
            provider-portal pattern — self-gates on user, no frozen AppShell edit. */}
        <AccountHealthBanner />
      </ElapsedSecondsContext.Provider>
    </TimeTrackingContext.Provider>
  );
}

export function useTimeTracking(): TimeTrackingContextType {
  const ctx = useContext(TimeTrackingContext);
  if (!ctx) throw new Error('useTimeTracking must be used within TimeTrackingProvider');
  return ctx;
}

/** The running timer's elapsed seconds — ticks once per second while a timer
 *  is active, 0 otherwise. SUBSCRIBING HERE MEANS RE-RENDERING EVERY SECOND;
 *  that is the deliberate cost of displaying the clock, and it is why this is
 *  a separate hook rather than a field on useTimeTracking() (perf 3d). Only
 *  components that render the elapsed time should call it. */
export function useElapsedSeconds(): number {
  return useContext(ElapsedSecondsContext);
}
